import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { INDUSTRIES, EXPLORER_IDS, OPPORTUNITIES, GOALS } from "@/lib/discovery";
import { prefersReducedMotion } from "@/lib/motion";

const TABS = EXPLORER_IDS.map((id) => INDUSTRIES.find((i) => i.id === id));
const WRAP = "mx-auto max-w-[1400px] px-5 md:px-8";

/**
 * Pick an industry, watch the workflow and the opportunities change.
 * A real tablist: arrow keys move, Home/End jump, panel is labelled by its tab.
 */
export default function IndustryExplorer({ initial = "dentist" }) {
  const [active, setActive] = useState(initial);
  const [lit, setLit] = useState(0);
  const tabRefs = useRef([]);
  const ind = TABS.find((t) => t.id === active) || TABS[0];

  // Light the workflow one node at a time whenever the industry changes.
  useEffect(() => {
    if (prefersReducedMotion()) { setLit(ind.flow.length); return undefined; }
    setLit(0);
    const timers = ind.flow.map((_, i) => setTimeout(() => setLit(i + 1), 220 + i * 260));
    return () => timers.forEach(clearTimeout);
  }, [ind]);

  const onKey = (e, i) => {
    const n = TABS.length;
    let ni = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") ni = (i + 1) % n;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") ni = (i - 1 + n) % n;
    if (e.key === "Home") ni = 0;
    if (e.key === "End") ni = n - 1;
    if (ni === null) return;
    e.preventDefault();
    setActive(TABS[ni].id);
    tabRefs.current[ni]?.focus();
  };

  return (
    <section className="border-b border-ink" id="industries" data-testid="section-industries">
      <div className={`${WRAP} section-pad`}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7">
            <p className="eyebrow">Industry examples</p>
            <h2 className="display mt-5 text-[34px] md:text-[52px] leading-[0.98]">
              Find your business below.
            </h2>
          </div>
          <p className="md:col-span-5 text-lg max-w-[40ch]">
            <b className="font-semibold text-ink">Pick the one closest to yours.</b>
            <span className="block mt-2 text-[15px] text-ink/65">
              The workflow and the opportunities change with it.
            </span>
          </p>
        </div>

        <div className="mt-8 flex flex-wrap border border-ink" role="tablist" aria-label="Choose an industry">
          {TABS.map((t, i) => (
            <button
              key={t.id}
              ref={(el) => (tabRefs.current[i] = el)}
              role="tab"
              id={`ind-tab-${t.id}`}
              aria-selected={active === t.id}
              aria-controls="ind-panel"
              tabIndex={active === t.id ? 0 : -1}
              onClick={() => setActive(t.id)}
              onKeyDown={(e) => onKey(e, i)}
              className={`px-4 py-3 text-[13px] md:text-[14px] font-medium uppercase tracking-[0.08em] border-r border-ink last:border-r-0 transition-colors ${active === t.id ? "bg-cobalt text-paper" : "hover:bg-surface"}`}
              data-testid={`ind-tab-${t.id}`}
            >
              {t.short}
            </button>
          ))}
        </div>

        <div id="ind-panel" role="tabpanel" aria-labelledby={`ind-tab-${ind.id}`} className="mt-6" key={ind.id}>
          <p className="eyebrow mb-3">How work moves with AUT labs, {ind.short.toLowerCase()}</p>
          <ol className="chain" aria-label="Workflow">
            {ind.flow.map((step, i) => (
              <li key={step} className="chain__node" data-on={lit > i}>
                <i>{String(i + 1).padStart(2, "0")}</i>
                {step}
              </li>
            ))}
          </ol>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-3">The problem, and what we would build</p>
              <div className="border-t border-ink">
                {ind.defaults.map((id, i) => {
                  const o = OPPORTUNITIES[id];
                  const goal = GOALS.find((g) => g.id === o.category);
                  return (
                    <div key={id} className="step-enter grid grid-cols-1 sm:grid-cols-[1fr_auto_1.2fr] gap-2 sm:gap-6 items-baseline py-3 border-b border-ink/25" style={{ animationDelay: `${i * 70}ms` }}>
                      <span className="text-[17px] font-medium">{o.problem}</span>
                      <span className="hidden sm:block text-ember-deep" aria-hidden="true">→</span>
                      <span className="text-[15px] text-ink/80">
                        {o.builds.slice(0, 2).join(" + ")}
                        <span className="eyebrow ml-3 text-[10px]">{goal.label}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-5">
              <p className="eyebrow mb-3">The pattern we usually find</p>
              <dl className="border-t border-ink">
                {ind.signals.map(([k, v]) => (
                  <div key={k} className="step-enter flex justify-between gap-4 py-3 border-b border-ink/25">
                    <dt className="text-[15px]">{k}</dt>
                    <dd className="text-[15px] text-ink/60 text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-2 text-[12px] text-ink/50">Illustrative pattern, not a client figure.</p>
              <Link to={`/find-opportunities?industry=${ind.id}`} className="pill pill-cobalt mt-6" data-testid="ind-cta">
                Find my opportunities <ArrowRight size={16} />
              </Link>
              <p className="mt-3 text-[13px] text-ink/60">
                Five questions, about two minutes. You get an opportunity map and an
                impact estimate on screen. Free, and yours to keep.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
